import { throwApiError } from '@/services/core/apiError'
import { adServerBase } from '@/services/creatives/previewApi'

/** What kind of ad the diagnostic request asks for — UII's "Request type". */
export type DiagnosticRequestType = 'NORMAL' | 'NATIVE' | 'VAST'

export const DIAGNOSTIC_REQUEST_TYPES: DiagnosticRequestType[] = ['NORMAL', 'NATIVE', 'VAST']

/** The ad server's tag type for each request type; a normal request sends none. */
const TAG_TYPES: Record<DiagnosticRequestType, string | undefined> = {
  NORMAL: undefined,
  NATIVE: 'native',
  VAST: 'vast',
}

/** How far a line item got through the auction, best first. */
export type AuctionStage =
  | 'WINNING'
  | 'SELECTED'
  | 'ELIGIBLE'
  | 'UNSUITABLE'
  | 'INELIGIBLE'
  | 'INACTIVE'

export const AUCTION_STAGES: AuctionStage[] = [
  'WINNING',
  'SELECTED',
  'ELIGIBLE',
  'UNSUITABLE',
  'INELIGIBLE',
  'INACTIVE',
]

export interface DiagnosticKeyValue {
  key: string
  values: string[]
}

/** The request to make on every chosen ad unit, as UII's diagnostics form takes it. */
export interface DiagnosticRequest {
  requestType: DiagnosticRequestType
  /** Narrows the output to one line item; the auction itself still runs in full. */
  lineItemId?: string
  categories: string[]
  segments: string[]
  keywords: string[]
  keyValues: DiagnosticKeyValue[]
  latitude?: number
  longitude?: number
  /** The page the request claims to come from. */
  context?: string
  ip?: string
}

/** One line item's run through the auction, from `ParticipantDiagnostic` in core. */
export interface DiagnosticParticipant {
  internalId: number
  /** The line item id. */
  externalId: string
  reachedStage: AuctionStage
  matchedExpressions: string[]
  negatedMatchedExpressions: string[]
  unmatchedExpressionTypes: Record<string, number>
  negatedUnmatchedExpressions: string[]
  unmatchedFilters: Record<string, string[]>
  hasUnapprovedPoliticalAds: boolean
  inactiveAdCount: number
  blockedAdCount: number
  unmatchedAdCount: number
  matchedAdCount: number
  competingExternalAds: string[]
  winningAds: string[]
  lossReasons: string[]
  blocklistDiagnostics: string[]
  floorDiagnostics: unknown[]
}

/** The ad server's answer for one ad unit, from `DiagnosticAdUnit` in core. */
export interface DiagnosticResult {
  requestTargets: string[]
  country: string | null
  city: string | null
  temperature: string | null
  temperatureLocation: string | null
  organisation: string | null
  totalParticipants: number
  politicalApprovalRequired: boolean
  lineItemParticipants: DiagnosticParticipant[]
  /** The cheapest winning bid as CPM, e.g. "NOK 12.5". */
  entryCost: string
}

/** Every value of a list parameter is sent as its own repeat of the parameter. */
function appendAll(params: URLSearchParams, name: string, values: string[]) {
  for (const value of values) {
    const trimmed = value.trim()
    if (trimmed) params.append(name, trimmed)
  }
}

/**
 * The ad server has been known to put control characters in the diagnostic
 * JSON, which `JSON.parse` rejects, so they are stripped first — as UII does.
 */
function parseDiagnostic(text: string): DiagnosticResult {
  // eslint-disable-next-line no-control-regex
  return JSON.parse(text.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, ''))
}

export const DiagnosticsApi = {
  /**
   * Runs a diagnostic ad request on one ad unit: the ad server holds the auction
   * as for a real request, records nothing, and reports how far every line item
   * got and why. The ad server is public, so no credentials are sent.
   */
  run: async (auId: string, request: DiagnosticRequest): Promise<DiagnosticResult> => {
    const params = new URLSearchParams({
      format: 'dx',
      auId,
      // The browser's own offset, as UII sends unless told otherwise.
      tzo: String(new Date().getTimezoneOffset()),
      gdpr: '0',
    })
    const tagType = TAG_TYPES[request.requestType]
    if (tagType) params.set('tt', tagType)
    if (request.lineItemId) params.set('dxLineItemId', request.lineItemId)
    appendAll(params, 'c', request.categories)
    appendAll(params, 'segments', request.segments)
    appendAll(params, 'keywords', request.keywords)
    const keyValues = request.keyValues
      .map((kv) => ({ key: kv.key.trim(), values: kv.values.map((v) => v.trim()).filter(Boolean) }))
      .filter((kv) => kv.key && kv.values.length)
    // One parameter holding a JSON list of single-key maps, the shape the ad
    // server reads. Without it, unknown parameters are taken as key-values.
    if (keyValues.length)
      params.set('kv', JSON.stringify(keyValues.map((kv) => ({ [kv.key]: kv.values }))))
    if (request.latitude !== undefined && request.longitude !== undefined) {
      params.set('latitude', String(request.latitude))
      params.set('longitude', String(request.longitude))
    }
    if (request.context?.trim()) params.set('ctx', request.context.trim())
    if (request.ip?.trim()) params.set('ip', request.ip.trim())

    const res = await fetch(`${adServerBase()}/i?${params}`)
    if (!res.ok) await throwApiError(res, 'Failed to run diagnostics')
    return parseDiagnostic(await res.text())
  },
}
